import { useMemo } from "react";
import { Compass } from "lucide-react";

const HINTS = {
  physically_sick: "Illness got in the way most often. Build in a 'sick-day version' of the habit — even 1 minute counts.",
  forgot: "Forgetting is the top blocker. Try anchoring the habit to something you already do (after coffee, before bed).",
  no_time: "Schedule is the main obstacle. Consider shrinking the habit or protecting a fixed slot in the calendar.",
  emergency: "Emergencies came up. That's life — keep the goal gentle after disruptions and restart small.",
  mental_emotional: "Emotional load is the biggest blocker. Worth discussing with your coach; pair the habit with a mood check.",
  other: "Mixed reasons. Look at the notes below for the pattern.",
};

export function ReflectionInsights({ insights, history = [], forClinician = false }) {
  const ranked = useMemo(() => (insights?.counts || []).filter((c) => c.count > 0).sort((a, b) => b.count - a.count), [insights]);
  if (!insights || insights.total === 0) {
    return (
      <div className="text-sm text-stone-500">
        No reflections yet. When a week doesn't land, {forClinician ? "the member can optionally jot down" : "you can optionally jot down"} what got in the way on the Progress tab — never required. Any notes show up here as patterns.
      </div>
    );
  }
  const max = Math.max(...insights.counts.map((c) => c.count), 1);
  return (
    <div className="space-y-4" data-testid="reflection-insights">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-stone-800">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1">
          <Compass className="w-3.5 h-3.5" /> Pattern
        </div>
        {HINTS[insights.top_reason]}
      </div>
      <ul className="space-y-2">
        {ranked.map((c) => (
          <li key={c.reason} data-testid={`insight-${c.reason}`}>
            <div className="flex justify-between text-xs text-stone-600 mb-1">
              <span>{c.label}</span>
              <span className="font-mono">{c.count}</span>
            </div>
            <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
      {history.length > 0 && (
        <ul className="space-y-2 max-h-40 overflow-y-auto pt-2 border-t border-stone-100">
          {history.slice(0, 8).map((r) => (
            <li key={r.id} className="text-xs text-stone-600 border-l-2 border-amber-300 pl-2">
              <span className="font-semibold text-stone-800">{r.habit_title}</span> · {r.week} · {r.reason_label}
              {r.detail && <span className="block text-stone-500 mt-0.5">"{r.detail}"</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
